/**
 * Platform adapters. The web build uses browser APIs; a Capacitor build can
 * swap these for native implementations (e.g. @capacitor/preferences) without
 * touching feature code. Every adapter degrades gracefully when unavailable.
 */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeLocalStorage(): Storage | null {
  try {
    const probe = '__mt_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

const memory = new Map<string, string>();
const memoryStorage: KeyValueStorage = {
  getItem: (k) => memory.get(k) ?? null,
  setItem: (k, v) => void memory.set(k, v),
  removeItem: (k) => void memory.delete(k),
};

/** Small, synchronous app state (answer queue, device preferences). */
export const localStore: KeyValueStorage = (typeof window !== 'undefined' && safeLocalStorage()) || memoryStorage;

/** Storage for the Supabase auth session. */
export const authStorage: KeyValueStorage = localStore;

export function isNativeShell(): boolean {
  const cap = typeof window !== 'undefined' ? (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor : undefined;
  return Boolean(cap?.isNativePlatform?.());
}

/** Device-level preferences that are not account data. */
export const devicePrefs = {
  get<T>(key: string, fallback: T): T {
    try {
      const raw = localStore.getItem(`mt:pref:${key}`);
      return raw === null ? fallback : (JSON.parse(raw) as T);
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown): void {
    localStore.setItem(`mt:pref:${key}`, JSON.stringify(value));
  },
};
